import { useState, useEffect } from "react";
import { API, useAuth } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Brain, Search, Loader2, BookOpen, ChevronRight, ChevronDown,
  Lightbulb, FlaskConical, AlertTriangle, GraduationCap, Database,
  Table2, FileText, Beaker, HelpCircle, BookMarked, Lock, ShoppingBag
} from "lucide-react";

const VOLUME_LABELS = {
  1: "Vol 1: The Regulatory Atlas",
  2: "Vol 2: The Mathematical Toolbox",
  3: "Vol 3: Diagnostics & Verification",
  4: "Vol 4: Case Studies in Applied Leadership",
};

const CATEGORY_ICONS = {
  chapter: BookOpen,
  case_study: FileText,
  lab_simulation: Beaker,
  qa: HelpCircle,
  quick_reference: BookMarked,
  foreword: BookOpen,
  appendix: BookMarked,
};

const CATEGORY_COLORS = {
  chapter: "text-[#1A6BCC]",
  case_study: "text-amber-400",
  lab_simulation: "text-emerald-400",
  qa: "text-purple-400",
  quick_reference: "text-cyan-400",
  foreword: "text-slate-400",
  appendix: "text-slate-400",
};

const KnowledgeBasePage = () => {
  const { t } = useTranslation('modules');
  const { user } = useAuth();
  const navigate = useNavigate();
  const [chapters, setChapters] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [seriesFilter, setSeriesFilter] = useState("");
  const [volumeFilter, setVolumeFilter] = useState("");
  const [expandedNode, setExpandedNode] = useState(null);
  const [nodeDetail, setNodeDetail] = useState(null);

  useEffect(() => {
    const params = {};
    if (seriesFilter) params.series = seriesFilter;
    if (volumeFilter) params.volume = volumeFilter;
    if (seriesFilter === "III" && !volumeFilter) params.lang = "en";

    Promise.all([
      axios.get(`${API}/kb/explore`, { params, withCredentials: true }),
      axios.get(`${API}/kb/stats`, { withCredentials: true }),
    ]).then(([exploreRes, statsRes]) => {
      setChapters(exploreRes.data.chapters || []);
      setStats(statsRes.data);
      setAccessDenied(false);
      setLoading(false);
    }).catch((err) => {
      if (err.response?.status === 403) {
        setAccessDenied(true);
      }
      setLoading(false);
    });
  }, [seriesFilter, volumeFilter]);

  const doSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const params = { q: searchQuery, limit: 30 };
      if (seriesFilter) params.series = seriesFilter;
      const res = await axios.get(`${API}/kb/search`, { params });
      setSearchResults(res.data.results || []);
    } catch {}
    setSearching(false);
  };

  const openNode = async (nodeId) => {
    if (expandedNode === nodeId) { setExpandedNode(null); setNodeDetail(null); return; }
    setExpandedNode(nodeId);
    try {
      const res = await axios.get(`${API}/kb/node/${nodeId}`);
      setNodeDetail(res.data);
    } catch {}
  };

  if (loading) return <div className="min-h-screen bg-[#0A1628] flex items-center justify-center"><Loader2 className="w-8 h-8 text-[#1A6BCC] animate-spin" /></div>;

  if (accessDenied) return (
    <div className="min-h-screen bg-[#0A1628] flex items-center justify-center" data-testid="kb-access-denied">
      <div className="max-w-md text-center p-8">
        <div className="w-16 h-16 bg-[#1E3A5F]/50 rounded-2xl flex items-center justify-center mx-auto mb-6">
          <Lock className="w-8 h-8 text-[#F59E0B]" />
        </div>
        <h2 className="text-xl font-bold text-white mb-3">Knowledge Base Access Required</h2>
        <p className="text-[#8B95A5] text-sm leading-relaxed mb-6">
          The CHARSTAN Knowledge Base is available exclusively to members who have purchased our engineering quality series. 
          Access includes all volumes, case studies, lab simulations, and reference materials.
        </p>
        <div className="flex flex-col gap-3">
          <button
            onClick={() => navigate("/publishing?tab=where-to-buy")}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-[#1A6BCC] hover:bg-[#1558B0] text-white rounded-[3px] text-sm font-medium transition-colors"
            data-testid="kb-where-to-buy-btn"
          >
            <ShoppingBag size={16} /> Where to Buy
          </button>
          <button
            onClick={() => navigate("/publishing")}
            className="flex items-center justify-center gap-2 px-6 py-3 bg-transparent border border-[rgba(255,255,255,0.1)] hover:bg-[#1E3A5F]/30 text-[#CBD5E1] rounded-[3px] text-sm transition-colors"
            data-testid="kb-bookshelf-btn"
          >
            <BookOpen size={16} /> Browse Bookshelf
          </button>
        </div>
        <p className="text-[#64748B] text-xs mt-6">
          Already purchased? Contact your administrator to activate access.
        </p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A1628] text-white" data-testid="knowledge-base-page">
      {/* Header */}
      <div className="border-b border-[#1E3A5F]/50 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#1A6BCC] rounded flex items-center justify-center">
              <Brain className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="font-bold text-sm">Knowledge Base</h1>
              <p className="text-[10px] text-slate-500">{stats?.total_nodes || 0} knowledge nodes across {Object.keys(stats?.by_series || {}).length} series</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <select
              value={seriesFilter}
              onChange={(e) => { setSeriesFilter(e.target.value); setVolumeFilter(""); }}
              className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white focus:outline-none"
              data-testid="series-filter"
            >
              <option value="">All Series</option>
              <option value="I">Series I</option>
              <option value="II">Series II</option>
              <option value="III">Series III</option>
            </select>
            {seriesFilter === "III" && (
              <select
                value={volumeFilter}
                onChange={(e) => setVolumeFilter(e.target.value)}
                className="bg-[#0D1F38] border border-[#1E3A5F] rounded px-2 py-1 text-xs text-white focus:outline-none"
                data-testid="volume-filter"
              >
                <option value="">All Volumes</option>
                <option value="1">Vol 1: Regulatory Atlas</option>
                <option value="2">Vol 2: Mathematical Toolbox</option>
                <option value="3">Vol 3: Diagnostics</option>
                <option value="4">Vol 4: Case Studies</option>
              </select>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-6">
        {/* Search */}
        <div className="flex gap-2 mb-6" data-testid="kb-search">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && doSearch()}
            placeholder={t('platform:knowledgeBase.searchPlaceholder')}
            className="flex-1 bg-[#0D1F38] border border-[#1E3A5F] rounded px-3 py-2 text-sm text-white placeholder:text-slate-600 focus:border-[#1A6BCC] focus:outline-none"
            data-testid="kb-search-input"
          />
          <button onClick={doSearch} disabled={searching} className="bg-[#1A6BCC] px-4 rounded text-sm font-bold" data-testid="kb-search-btn">
            {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </div>

        {/* Stats cards */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="kb-stats">
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3">
              <div className="text-xl font-bold text-white">{stats.total_nodes}</div>
              <div className="text-[10px] text-slate-500">Total Nodes</div>
            </div>
            {Object.entries(stats.by_series).map(([s, c]) => (
              <div key={s} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3 cursor-pointer hover:border-[#1A6BCC]/40 transition-colors" onClick={() => { setSeriesFilter(s); setVolumeFilter(""); }}>
                <div className="text-xl font-bold text-[#00C8E0]">{c}</div>
                <div className="text-[10px] text-slate-500">Series {s}</div>
              </div>
            ))}
            <div className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3">
              <div className="text-xl font-bold text-emerald-400">{stats.categories?.length || 0}</div>
              <div className="text-[10px] text-slate-500">Categories</div>
            </div>
          </div>
        )}

        {/* Series III Volume Cards */}
        {seriesFilter === "III" && stats?.series3_volumes && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6" data-testid="s3-volume-cards">
            {[1,2,3,4].map(v => (
              <button
                key={v}
                onClick={() => setVolumeFilter(volumeFilter === String(v) ? "" : String(v))}
                className={`bg-[#0D1F38] border rounded-lg p-3 text-left transition-colors ${volumeFilter === String(v) ? "border-[#1A6BCC]" : "border-[#1E3A5F] hover:border-[#1E3A5F]/80"}`}
                data-testid={`s3-vol-${v}`}
              >
                <div className="text-lg font-bold text-white">{stats.series3_volumes[`Vol${v}`] || 0}</div>
                <div className="text-[10px] text-slate-500">{VOLUME_LABELS[v]}</div>
              </button>
            ))}
          </div>
        )}

        {/* Search results */}
        {searchResults.length > 0 && (
          <div className="mb-6">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">Search Results ({searchResults.length})</h2>
            <div className="space-y-2">
              {searchResults.slice(0, 20).map((r, i) => (
                <button
                  key={i}
                  onClick={() => openNode(r.node_id)}
                  className="w-full text-left bg-[#0D1F38] border border-[#1E3A5F] rounded-lg p-3 hover:border-[#1A6BCC]/40 transition-colors"
                  data-testid={`search-result-${i}`}
                >
                  <div className="text-[10px] text-[#00C8E0] mb-1">
                    Series {r.series}
                    {r.volume ? ` · ${VOLUME_LABELS[r.volume] || `Vol ${r.volume}`}` : r.chapter ? ` · Ch.${r.chapter}` : ""}
                    {r.lang === "pt-br" && <span className="ml-1 text-amber-400">(PT-BR)</span>}
                  </div>
                  <div className="text-sm font-bold text-white mb-1">{r.topic || r.section_title}</div>
                  <div className="text-xs text-slate-400">{r.category}</div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Chapter explorer */}
        <h2 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
          <Database className="w-3.5 h-3.5" /> Browse by {seriesFilter === "III" ? "Volume & Chapter" : "Chapter"}
        </h2>
        <div className="space-y-2" data-testid="kb-chapters">
          {chapters.map((ch, ci) => {
            const CatIcon = (ch.category && CATEGORY_ICONS[ch.category]) || BookOpen;
            const catColor = (ch.category && CATEGORY_COLORS[ch.category]) || "text-[#1A6BCC]";

            return (
              <div key={ci} className="bg-[#0D1F38] border border-[#1E3A5F] rounded-lg overflow-hidden">
                <div className="p-4 flex items-center justify-between">
                  <div>
                    <div className="text-[10px] text-[#00C8E0] uppercase tracking-wider mb-1 flex items-center gap-1.5">
                      {ch.series === "III" ? (
                        <>Series III · {VOLUME_LABELS[ch.volume] || `Volume ${ch.volume}`}</>
                      ) : (
                        <>Series {ch.series} · Chapter {ch.chapter}</>
                      )}
                      {ch.lang === "pt-br" && <span className="text-amber-400">(PT-BR)</span>}
                    </div>
                    <h3 className="text-sm font-bold text-white flex items-center gap-2">
                      <CatIcon className={`w-3.5 h-3.5 ${catColor}`} />
                      {ch.chapter_title || ch.category}
                    </h3>
                    <div className="text-[10px] text-slate-500 mt-0.5">{ch.category} · {ch.sections?.length || 0} sections</div>
                  </div>
                </div>
                <div className="border-t border-[#1E3A5F]/50 bg-[#0A1628]/50">
                  {ch.sections?.map((sec, si) => (
                    <div key={si}>
                      <button
                        onClick={() => openNode(sec.node_id)}
                        className="w-full text-left px-4 py-2.5 flex items-center gap-3 hover:bg-[#132340]/50 transition-colors border-b border-[#1E3A5F]/30"
                        data-testid={`node-${sec.node_id}`}
                      >
                        <div className="w-6 h-6 rounded bg-[#132340] flex items-center justify-center text-[10px] text-slate-400 font-mono shrink-0">{sec.section}</div>
                        <div className="flex-1 min-w-0">
                          <div className="text-xs text-white truncate">{sec.section_title || sec.topic}</div>
                          <div className="text-[10px] text-slate-500 flex items-center gap-2">
                            {sec.concept_count > 0 && <span>{sec.concept_count} concepts</span>}
                            {sec.has_formulas && <span className="text-[#00C8E0]">formulas</span>}
                            {sec.has_worked_example && <span className="text-emerald-400">worked example</span>}
                            {sec.has_tables && <span className="text-amber-400">tables</span>}
                          </div>
                        </div>
                        <ChevronRight className={`w-3.5 h-3.5 text-slate-600 shrink-0 transition-transform ${expandedNode === sec.node_id ? "rotate-90" : ""}`} />
                      </button>

                      {/* Expanded node detail */}
                      {expandedNode === sec.node_id && nodeDetail && (
                        <NodeDetailPanel nodeDetail={nodeDetail} nodeId={sec.node_id} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const NodeDetailPanel = ({ nodeDetail, nodeId }) => {
  const isSeriesIII = nodeDetail.series === "III";

  return (
    <div className="px-4 py-4 pl-13 bg-[#0D1F38] border-t border-[#1E3A5F]/30 space-y-4" data-testid={`node-detail-${nodeId}`}>
      {/* Series III: show full content */}
      {isSeriesIII && nodeDetail.content && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <BookOpen className="w-3.5 h-3.5 text-[#1A6BCC]" />
            <span className="text-[10px] font-bold text-slate-400 uppercase">Published Content</span>
            <span className="text-[10px] text-slate-600 ml-auto">{nodeDetail.source}</span>
          </div>
          <div className="text-xs text-slate-300 bg-[#0A1628] rounded px-3 py-3 leading-relaxed whitespace-pre-wrap max-h-96 overflow-y-auto" data-testid="node-content">
            {nodeDetail.content}
          </div>
        </div>
      )}

      {/* Series III: tables */}
      {isSeriesIII && nodeDetail.tables?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2">
            <Table2 className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-[10px] font-bold text-slate-400 uppercase">Tables ({nodeDetail.tables.length})</span>
          </div>
          {nodeDetail.tables.map((tbl, i) => (
            <div key={i} className="text-xs font-mono text-slate-300 bg-[#0A1628] rounded px-3 py-2 mb-2 overflow-x-auto whitespace-pre-wrap" data-testid={`node-table-${i}`}>
              {tbl}
            </div>
          ))}
        </div>
      )}

      {/* Series I/II: concepts */}
      {!isSeriesIII && nodeDetail.concepts?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2"><Lightbulb className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] font-bold text-slate-400 uppercase">Concepts</span></div>
          <div className="space-y-1">{nodeDetail.concepts.map((c, i) => <div key={i} className="text-xs text-slate-300 bg-[#0A1628] rounded px-3 py-2">{c}</div>)}</div>
        </div>
      )}

      {/* Series I/II: formulas */}
      {!isSeriesIII && nodeDetail.formulas?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2"><FlaskConical className="w-3.5 h-3.5 text-[#00C8E0]" /><span className="text-[10px] font-bold text-slate-400 uppercase">Formulas</span></div>
          <div className="space-y-1">{nodeDetail.formulas.map((f, i) => <div key={i} className="text-xs bg-[#132340] rounded px-3 py-2"><span className="text-[#00C8E0]">{f.name}:</span> <span className="font-mono text-white" dangerouslySetInnerHTML={{__html: f.formula}} /></div>)}</div>
        </div>
      )}

      {/* Series I/II: auditor tips */}
      {!isSeriesIII && nodeDetail.auditor_tips?.length > 0 && (
        <div>
          <div className="flex items-center gap-1.5 mb-2"><AlertTriangle className="w-3.5 h-3.5 text-amber-400" /><span className="text-[10px] font-bold text-slate-400 uppercase">Auditor Tips</span></div>
          <div className="space-y-1">{nodeDetail.auditor_tips.map((t, i) => <div key={i} className="text-xs text-amber-200 bg-amber-900/10 rounded px-3 py-2">{t}</div>)}</div>
        </div>
      )}

      {/* Series I/II: worked example */}
      {!isSeriesIII && nodeDetail.worked_example && (
        <div>
          <div className="flex items-center gap-1.5 mb-2"><GraduationCap className="w-3.5 h-3.5 text-emerald-400" /><span className="text-[10px] font-bold text-slate-400 uppercase">Worked Example</span></div>
          <div className="bg-emerald-900/10 border border-emerald-800/20 rounded p-3">
            <div className="text-xs font-bold text-white mb-1">{nodeDetail.worked_example.title}</div>
            <div className="text-[10px] text-slate-400">{nodeDetail.worked_example.data}</div>
          </div>
        </div>
      )}

      {/* Series I/II: content fallback */}
      {!isSeriesIII && nodeDetail.content && !nodeDetail.concepts?.length && (
        <div>
          <div className="text-xs text-slate-300 bg-[#0A1628] rounded px-3 py-3 leading-relaxed whitespace-pre-wrap max-h-64 overflow-y-auto">
            {nodeDetail.content}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBasePage;
